// Stage 2D-1: one shared Staff Cost form (Salary / Bonus / Commission), entered manually by an Admin.
// Nothing is read from, linked to or created by Payroll, Attendance, Bonus, Commission or any other module.
// The database builds the debit/credit lines (acc_save_transaction → acc_staff_cost_prepare) and is the authority on
// every rule; this form only describes what happened.
import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Field, Input, Select, TextArea, Notice, DocumentUploader } from "./kit/components";
import { T, text, PAYMENT_METHODS } from "./kit/tokens";
import { showToast, useMobile } from "../ui/core";
import { previewTransaction, saveTransaction, accountLedger, attachFile } from "./api";
import { DualDateInput, errText } from "./shared";
import { BS_MONTHS } from "./bs";
import { npr, todayIso, partyRoleMatch, STAFF_TYPES, STAFF_MODES, STAFF_PAYABLE_ROLE, staffCostPayload, staffCostProblems } from "./workflow";

const EMPTY = { staff_cost_type: "", txn_date: "", amount: "", party_id: "", reference: "", description: "", operational_ref: "", relates_from: "", relates_to: "",
    relates_note: "", money_account_id: "", payment_date: "", payment_method: "bank_transfer", due_date: "" };

function Choice({ active, title, sub, onClick }) {
    return (
        <button type="button" onClick={onClick} aria-pressed={active}
            style={{ textAlign: "left", background: active ? T.surface2 : T.surface, border: `1px solid ${active ? T.primary : T.border}`, borderRadius: T.radius,
                padding: "12px 14px", cursor: "pointer", fontFamily: T.font, display: "grid", gap: 4, borderLeft: `3px solid ${active ? T.primary : T.border}` }}>
            <span style={{ fontSize: 14, fontWeight: 650, color: T.textStrong }}>{title}</span>
            {sub && <span style={{ fontSize: 12.5, color: T.textMuted, lineHeight: 1.45 }}>{sub}</span>}
        </button>
    );
}

// BS year-month picker for "relates to" (value "YYYY-MM"; information only).
function MonthPick({ value, onChange, years, label, optional }) {
    const [y, m] = (value || "").split("-");
    const set = (ny, nm) => onChange(ny && nm ? `${ny}-${String(nm).padStart(2, "0")}` : "");
    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <Select aria-label={`${label} month`} value={m ? Number(m) : ""} onChange={e => set(y || years[years.length - 1], e.target.value)}>
                <option value="">{optional ? "—" : "Month…"}</option>
                {BS_MONTHS.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
            </Select>
            <Select aria-label={`${label} year`} value={y || ""} onChange={e => set(e.target.value, m || 1)}>
                <option value="">Year…</option>
                {years.map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
        </div>
    );
}

export default function StaffCostForm({ open, ctx, initial, onClose, onSaved }) {
    const { data, cal, display } = ctx;
    const mobile = useMobile();
    const editing = !!initial?.id;
    const [step, setStep] = useState("choose");
    const [kind, setKind] = useState("");
    const [f, setF] = useState(EMPTY);
    const [files, setFiles] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState("");
    const [dup, setDup] = useState(null);
    const [dupReason, setDupReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [owed, setOwed] = useState(null);
    const reqId = useRef(null);

    useEffect(() => {
        if (!open) return;
        reqId.current = crypto.randomUUID();
        const t = initial || {};
        const firstBank = data.moneyAccounts.find(m => m.active && m.kind === "bank") || data.moneyAccounts.find(m => m.active);
        setKind(t.kind && t.kind.startsWith("staff_cost_") ? t.kind : "");
        setStep(t.kind && t.staff_cost_type ? "details" : "choose");
        setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, t[k] == null ? EMPTY[k] : String(t[k])])),
            txn_date: t.txn_date || todayIso(), money_account_id: t.money_account_id || firstBank?.id || "", payment_method: t.payment_method || "bank_transfer" });
        setFiles([]); setPreview(null); setPreviewErr(""); setDup(null); setDupReason(""); setErr(""); setBusy(false); setOwed(null);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k) => (e) => { const v = e?.target ? e.target.value : e; setF(x => ({ ...x, [k]: v })); setDup(null); };
    const rolesOf = useMemo(() => { const m = {}; data.roles.forEach(r => { (m[r.party_id] ||= []).push(r.role); }); return m; }, [data.roles]);
    const staff = data.parties.filter(p => p.active && partyRoleMatch(rolesOf[p.id] || [], "staff"));
    const years = useMemo(() => { const ys = cal.years.slice().sort(); return ys.length ? ys : [2082, 2083]; }, [cal]);
    const paid = kind && kind !== "staff_cost_owed";
    const typeLabel = (STAFF_TYPES.find(([k]) => k === f.staff_cost_type) || [])[1] || "Staff cost";
    const modeLabel = (STAFF_MODES.find(([k]) => k === kind) || [])[1] || "";

    // Salary: "relates to" starts at the accounting month (information only; the accounting date decides the period).
    useEffect(() => {
        if (step !== "details" || f.staff_cost_type !== "salary" || f.relates_from || editing) return;
        const b = cal.adToBs(f.txn_date);
        if (b) setF(x => ({ ...x, relates_from: `${b.y}-${String(b.m).padStart(2, "0")}` }));
    }, [step, f.staff_cost_type]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {   // still owed to the chosen person for this type (posted balance of that type's payable account)
        if (kind !== "staff_cost_payment" || !f.party_id || !f.staff_cost_type) { setOwed(null); return; }
        const acc = data.mappings.find(m => m.role === STAFF_PAYABLE_ROLE[f.staff_cost_type])?.account_id;
        if (!acc) { setOwed(null); return; }
        accountLedger(acc, f.party_id, "2000-01-01", todayIso()).then(rows => setOwed(rows.length ? Number(rows[rows.length - 1].balance) : 0)).catch(() => setOwed(null));
    }, [kind, f.party_id, f.staff_cost_type]); // eslint-disable-line react-hooks/exhaustive-deps

    const payload = () => ({ ...staffCostPayload(f, kind), id: initial?.id || null, expected_updated_at: initial?.updated_at || null });
    const problems = step === "details" ? staffCostProblems(f, kind) : [];

    useEffect(() => {   // live preview from the database (none while saving)
        if (busy) return;
        if (step !== "details" || !(Number(f.amount) > 0) || !f.party_id || !f.staff_cost_type) { setPreview(null); setPreviewErr(""); return; }
        const t = setTimeout(() => {
            previewTransaction(payload()).then(p => { setPreview(p); setPreviewErr(""); }).catch(e => { setPreview(null); setPreviewErr(errText(e)); });
        }, 350);
        return () => clearTimeout(t);
    }, [step, kind, f, busy]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async (markReady) => {
        setErr("");
        if (problems.length) { setErr(problems[0]); return; }
        setBusy(true);
        try {
            const res = await saveTransaction({ ...payload(), client_request_id: reqId.current, mark_ready: markReady, duplicate_reason: dup ? dupReason.trim() || null : null });
            for (const file of files) await attachFile(res.entry_id, file);
            showToast(markReady ? "Sent for review" : "Draft saved");
            await onSaved(res);
            return;
        } catch (e) {
            const m = errText(e);
            if (m.startsWith("POSSIBLE_DUPLICATE")) { setDup(m.replace(/^POSSIBLE_DUPLICATE:\s*/, "")); setErr(""); }
            else setErr(m);
        }
        setBusy(false);
    };
    const dupBlocked = !!dup && !dupReason.trim();

    return (
        <Modal open={open} width={step === "details" ? 960 : 640} onClose={busy ? undefined : onClose}
            title={step === "choose" ? "Staff cost" : `${typeLabel} — ${modeLabel.toLowerCase()}`}
            subtitle={step === "choose" ? "Salary, bonus or commission, entered by hand. Nothing is taken from Payroll or any other module."
                : "The accounting date decides the period. The payment date and \"relates to\" are information only."}
            footer={step === "details" ? <>
                {!editing && <Button variant="ghost" onClick={() => { setStep("choose"); setPreview(null); }}>← Back</Button>}
                <span style={{ flex: 1 }} />
                <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="secondary" onClick={() => save(false)} disabled={busy || dupBlocked}>Save draft</Button>
                <Button onClick={() => save(true)} disabled={busy || dupBlocked}>{busy ? "Saving…" : "Save & send for review"}</Button>
            </> : <>
                <span style={{ flex: 1 }} />
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button disabled={!f.staff_cost_type || !kind} onClick={() => setStep("details")}>Continue</Button>
            </>}>

            {step === "choose" && (
                <div style={{ display: "grid", gap: 16 }}>
                    <div style={{ display: "grid", gap: 8 }}>
                        <span style={text.label}>Type (required)</span>
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
                            {STAFF_TYPES.map(([k, l]) => <Choice key={k} active={f.staff_cost_type === k} title={l} onClick={() => setF(x => ({ ...x, staff_cost_type: k }))} />)}
                        </div>
                    </div>
                    <div style={{ display: "grid", gap: 8 }}>
                        <span style={text.label}>What happened?</span>
                        <div style={{ display: "grid", gap: 10 }}>
                            {STAFF_MODES.map(([k, l, sub]) => <Choice key={k} active={kind === k} title={l} sub={sub} onClick={() => setKind(k)} />)}
                        </div>
                    </div>
                </div>
            )}

            {step === "details" && (
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "minmax(0,1.15fr) minmax(0,1fr)", gap: 20, alignItems: "start" }}>
                    <div style={{ display: "grid", gap: 14 }}>
                        <Field label="Staff member" required hint={staff.length ? undefined : "Add the person under People & companies with the Staff role first."}>
                            <Select value={f.party_id} onChange={set("party_id")}>
                                <option value="">Choose…</option>
                                {staff.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </Select>
                        </Field>
                        {kind === "staff_cost_payment" && owed !== null && f.party_id && (
                            <div style={text.caption}>{typeLabel} still owed to this person: <b style={{ color: T.textStrong }}>{npr(owed)}</b></div>)}
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                            <Field label="Accounting date" required hint="Decides the accounting period.">
                                <DualDateInput value={f.txn_date} onChange={set("txn_date")} cal={cal} display={display} />
                            </Field>
                            {paid ? (
                                <Field label="Payment date" required hint="When the money was paid. Information only.">
                                    <DualDateInput value={f.payment_date} onChange={set("payment_date")} cal={cal} display={display} />
                                </Field>
                            ) : (
                                <Field label="Due date" hint="Optional."><Input type="date" value={f.due_date} onChange={set("due_date")} /></Field>
                            )}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                            <Field label="Amount (NPR)" required>
                                <Input type="number" inputMode="decimal" min="0" step="0.01" value={f.amount} onChange={set("amount")} style={{ fontSize: 16, fontWeight: 650, fontVariantNumeric: "tabular-nums" }} />
                            </Field>
                            {paid && (
                                <Field label="Paid from" required>
                                    <Select value={f.money_account_id} onChange={set("money_account_id")}>
                                        <option value="">Choose…</option>
                                        {data.moneyAccounts.filter(m => m.active).map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
                                    </Select>
                                </Field>
                            )}
                        </div>
                        {paid && (
                            <Field label="How it was paid">
                                <Select value={f.payment_method} onChange={set("payment_method")}>{PAYMENT_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
                            </Field>
                        )}
                        <Field label="Relates to" required={f.staff_cost_type === "salary"}
                            hint={`Nepali month(s) the ${typeLabel.toLowerCase()} is for. Information only: it never changes the accounting period.${f.staff_cost_type === "salary" ? "" : " Optional."}`}>
                            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 8 }}>
                                <MonthPick label="From" value={f.relates_from} years={years} optional={f.staff_cost_type !== "salary"} onChange={set("relates_from")} />
                                <MonthPick label="To" value={f.relates_to} years={years} optional onChange={set("relates_to")} />
                            </div>
                        </Field>
                        <Field label="Reference" required hint="Payslip, approval or voucher number."><Input value={f.reference} onChange={set("reference")} /></Field>
                        <Field label="Operational reference" hint="Optional free text, e.g. the payroll sheet it matches. Only a note: nothing is looked up or linked.">
                            <Input value={f.operational_ref} maxLength={200} onChange={set("operational_ref")} />
                        </Field>
                        <Field label="Description"><TextArea value={f.description} onChange={set("description")} placeholder="In your own words" /></Field>
                        <DocumentUploader optimize label="Payslip or approval (optional)" file={files[0] || null} onChange={(file) => setFiles(file ? [file] : [])} />
                    </div>

                    <div style={{ position: mobile ? "static" : "sticky", top: 0, display: "grid", gap: 12 }}>
                        <Preview preview={preview} err={previewErr} kind={kind} />
                        {!files.length && !editing && <Notice tone="info">No evidence attached. The reference is required; a payslip or approval is optional and can be added later. Review will show that none is attached.</Notice>}
                        {dup && (
                            <div role="alert" style={{ border: `2px solid ${T.warning}`, background: T.warningBg, borderRadius: T.radius, padding: 14, display: "grid", gap: 8 }}>
                                <b style={{ color: T.warning }}>This may already be recorded</b>
                                <span style={{ fontSize: 13, color: T.text }}>Similar: {dup.replace(/ — give a reason.*$/, "")}</span>
                                <span style={{ fontSize: 13, color: T.text }}>If it really is separate (for example arrears for the same month), say why. The reason is kept in the activity log.</span>
                                <TextArea value={dupReason} onChange={e => setDupReason(e.target.value)} placeholder="e.g. Arrears for the same month, approved separately" />
                            </div>
                        )}
                        {err && <Notice tone="danger">{err}</Notice>}
                    </div>
                </div>
            )}
        </Modal>
    );
}

function Preview({ preview, err, kind }) {
    const box = { background: T.surface2, border: `1px solid ${T.borderSoft}`, borderRadius: T.radius, padding: 14, display: "grid", gap: 10 };
    if (err) return <div style={box}><b style={{ fontSize: 13, color: T.textStrong }}>What this does to the books</b><span style={{ fontSize: 13, color: T.warning }}>{err}</span></div>;
    if (!preview) return <div style={box}><b style={{ fontSize: 13, color: T.textStrong }}>What this does to the books</b><span style={text.caption}>Fill in the details to see the accounting.</span></div>;
    return (
        <div style={box}>
            <b style={{ fontSize: 13, color: T.textStrong }}>What this does to the books</b>
            <span style={{ fontSize: 13, color: T.text }}>{kind === "staff_cost_owed" ? "No money moves yet. This records the cost and what the company owes."
                : kind === "staff_cost_payment" ? "Not a new cost: it pays what is owed." : "The cost and its payment, in one entry."}</span>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr>{["Account", "Debit", "Credit"].map((h, i) => <th key={h} style={{ textAlign: i ? "right" : "left", color: T.textMuted, fontWeight: 600, fontSize: 11, padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
                <tbody>{(preview.lines || []).map((l, i) => (
                    <tr key={i}>
                        <td style={{ padding: "6px 0", color: T.text }}>{l.code} {l.name}{l.party ? <span style={{ color: T.textMuted }}> · {l.party}</span> : null}</td>
                        <td style={{ textAlign: "right", ...text.money, fontSize: 13 }}>{Number(l.debit) ? npr(l.debit) : ""}</td>
                        <td style={{ textAlign: "right", ...text.money, fontSize: 13 }}>{Number(l.credit) ? npr(l.credit) : ""}</td>
                    </tr>))}</tbody>
            </table>
            <span style={text.caption}>Gets a {preview.series} voucher number when it is posted.</span>
            {(preview.duplicates || []).length > 0 && <span style={{ fontSize: 12, color: T.warning }}>Looks similar to: {preview.duplicates.map(d => d.display).join("; ")}</span>}
        </div>
    );
}
